import ENV from 'src/env.json';
import React from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import * as Sentry from '@sentry/react';
import { BrowserOptions } from '@sentry/browser/types/client';

function initSentry(): void {
  if (ENV.NODE_ENV === 'dev') {
    return;
  }

  const options: BrowserOptions = {
    dsn: ENV.SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: ENV.SENTRY_TRACES_SAMPLE_RATE,
    environment: ENV.NODE_ENV,
    // Ignore known browser/third-party errors we cannot fix
    ignoreErrors: [
      // Dynamic import/chunk load failures (network/cache issues)
      'ChunkLoadError',
      /Loading chunk .* failed/,
      /Failed to fetch dynamically imported module/,
      /Importing a module script failed/,
      /Unable to preload CSS/,
      // Fixes ADMIN-WEB-APP-DX: Dynamic module load errors
      /error loading dynamically imported module/,
      // Browser extension errors
      /runtime\.sendMessage/,
      'Invalid call to runtime.sendMessage',
      // MIME type errors from CDN/cache issues
      "'text/html' is not a valid JavaScript MIME type",
      // React minified errors (usually third-party or race conditions)
      /Minified invariant #/,
      /Minified React error #/,
      // Generic function errors from minified code
      't is not a function',
      // JSON parsing errors from network issues
      'Unexpected end of JSON input',
      /SyntaxError: Unexpected end of JSON/,
      // Promise rejection with non-Error objects (third-party libraries)
      /Object captured as promise rejection/,
      /Non-Error promise rejection/,
      // Fixes ADMIN-WEB-APP-CZ: CustomEvent unhandledrejection from third-party scripts
      /Event `CustomEvent`.*captured as promise rejection/,
      /CustomEvent.*unhandledrejection/,
      // Browser storage errors
      'InvalidStateError',
      /Failed to execute 'transaction' on 'IDBDatabase'/,
      /The operation is insecure/,
      // Fixes ADMIN-WEB-APP-E1: SecurityError localStorage access denied (privacy mode)
      /Failed to read the 'localStorage' property/,
      /Access is denied for this document/,
      // Browser quota/storage exceeded
      /QuotaExceededError/,
      /Storage quota exceeded/,
      // Browser file permission errors (NotReadableError)
      'NotReadableError',
      /The requested file could not be read/,
      // JWT decode errors from malformed tokens (third-party/corruption)
      /InvalidTokenError/,
      /Invalid token specified/,
      // Fixes ADMIN-WEB-APP-E0: FormMetadata.js from browser autofill extensions (1Password, etc.)
      /FormMetadata\.js/,
      /_logicalFormForControl/,
      // Fixes ADMIN-WEB-APP-DY: EmptyRanges from browser extensions (CodeMirror related)
      /Can't find variable: EmptyRanges/,
      // Note: ADMIN-WEB-APP-DZ (Network Error from user-guiding) filtered via denyUrls instead
      // Fixes ADMIN-WEB-APP-DH: CDN script load failures (dash.js, etc.)
      /Failed to load https:\/\/cdnjs\.cloudflare\.com/,
      // Fixes ADMIN-WEB-APP-E2, ADMIN-WEB-APP-E3: Profile fetch timeout (now handled gracefully)
      /Profile fetch timeout/,
    ],
    // Ignore errors from third-party scripts
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Firefox extensions
      /^moz-extension:\/\//i,
      // Safari extensions
      /^safari-extension:\/\//i,
      // Fixes ADMIN-WEB-APP-DZ: User-guiding analytics script
      /user-guiding/i,
      /userguiding/i,
      // Third-party CDNs that might fail - Fixes ADMIN-WEB-APP-DH
      /cdnjs\.cloudflare\.com/i,
    ],
  };

  const release = import.meta.env.VITE_REACT_APP_COMMIT_SHA;

  if (release) {
    options['release'] = release;
  } else {
    console.warn('Incomplete sentry configuration - REACT_APP_COMMIT_SHA env var is not defined');
  }

  Sentry.init(options);
}

export { initSentry };
